import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { servicesAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Loader2, X, Wrench, Search } from "lucide-react";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function CreateService() {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [serviceType, setServiceType] = useState("offering");
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    pricing_type: "fixed",
    price: "",
    currency: "USD",
    location: "",
    remote_ok: false,
    country: "",
    postal_code: "",
    travel_radius_miles: "",
    coverage_countrywide: false,
  });

  useEffect(() => {
    servicesAPI.getCategories().then((r) => setCats(r.data?.categories || [])).catch(() => setCats([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isRequest = serviceType === "request";
  const needsPrice = useMemo(() => {
    if (isRequest) return false; // budget optional
    return form.pricing_type !== "quote";
  }, [isRequest, form.pricing_type]);

  const addImages = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - images.length);
    const b64 = await Promise.all(files.map(fileToBase64));
    setImages((prev) => [...prev, ...b64]);
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Add a title");
    if (!form.category) return toast.error("Choose a category");
    if (!form.description.trim()) return toast.error("Add a description");
    if (needsPrice && !(Number(form.price) > 0)) return toast.error("Enter a price");

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        service_type: serviceType,
        pricing_type: isRequest ? null : form.pricing_type,
        price: form.price ? Number(form.price) : 0,
        currency: form.currency,
        location: form.location.trim(),
        remote_ok: form.remote_ok,
        country: form.country || null,
        postal_code: form.postal_code.trim(),
        travel_radius_miles:
          !isRequest && !form.coverage_countrywide && form.travel_radius_miles
            ? Number(form.travel_radius_miles)
            : null,
        coverage_countrywide: !isRequest && form.coverage_countrywide,
        images,
      };
      const res = await servicesAPI.createListing(payload);
      toast.success(isRequest ? "Request posted" : "Service posted");
      navigate(`/services/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post service");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <button onClick={() => navigate("/marketplace?tab=services")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to services
      </button>
      <h1 className="font-display text-3xl font-bold">Post a service</h1>

      <Card className="space-y-5 border-border bg-card p-5">
        <div>
          <Label className="mb-2 block">What do you want to do?</Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setServiceType("offering")}
              data-testid="service-type-offering"
              className={`flex items-center gap-2 rounded-lg border p-3 text-left text-sm font-semibold transition-colors ${serviceType === "offering" ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}
            >
              <Wrench className="h-4 w-4 text-primary" /> I'm offering a service
            </button>
            <button
              type="button"
              onClick={() => setServiceType("request")}
              data-testid="service-type-request"
              className={`flex items-center gap-2 rounded-lg border p-3 text-left text-sm font-semibold transition-colors ${serviceType === "request" ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}
            >
              <Search className="h-4 w-4 text-amber-500" /> I'm looking for help
            </button>
          </div>
        </div>

        <div>
          <Label htmlFor="svc-title">Title</Label>
          <Input id="svc-title" data-testid="service-title-input" value={form.title} onChange={(e) => set("title", e.target.value)}
            placeholder={isRequest ? "e.g. Looking for someone to set up my GT3 for Le Mans" : "e.g. Pro iRacing GT3 setups"} className="mt-1" />
        </div>

        <div>
          <Label>Category</Label>
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger className="mt-1" data-testid="service-category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
            <SelectContent>
              {cats.map((c) => <SelectItem key={c.id} value={c.slug}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="svc-desc">Description</Label>
          <Textarea id="svc-desc" data-testid="service-description-input" value={form.description} onChange={(e) => set("description", e.target.value)}
            placeholder="Describe the service, turnaround, what's included..." rows={5} className="mt-1" />
        </div>

        {!isRequest ? (
          <div>
            <Label className="mb-2 block">Pricing</Label>
            <RadioGroup value={form.pricing_type} onValueChange={(v) => set("pricing_type", v)} className="grid grid-cols-3 gap-2">
              {[["fixed", "Fixed"], ["hourly", "Hourly"], ["quote", "Contact for quote"]].map(([v, l]) => (
                <label key={v} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2.5 text-sm ${form.pricing_type === v ? "border-primary" : "border-border"}`}>
                  <RadioGroupItem value={v} data-testid={`service-pricing-${v}`} /> {l}
                </label>
              ))}
            </RadioGroup>
            {form.pricing_type !== "quote" && (
              <div className="mt-3">
                <Label htmlFor="svc-price">{form.pricing_type === "hourly" ? "Hourly rate (USD)" : "Price (USD)"}</Label>
                <Input id="svc-price" data-testid="service-price-input" type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1" />
              </div>
            )}
          </div>
        ) : (
          <div>
            <Label htmlFor="svc-budget">Budget (USD, optional)</Label>
            <Input id="svc-budget" data-testid="service-budget-input" type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="Leave blank for open budget" className="mt-1" />
          </div>
        )}

        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Can be done remotely / online</p>
              <p className="text-xs text-muted-foreground">No travel needed — shows everywhere</p>
            </div>
            <Switch checked={form.remote_ok} onCheckedChange={(v) => set("remote_ok", v)} data-testid="service-remote-switch" />
          </div>
        </div>

        {/* Location & travel coverage */}
        <div className="space-y-3">
          <Label>Location</Label>
          <div className="grid gap-3 sm:grid-cols-2">
            <Select value={form.country || "none"} onValueChange={(v) => set("country", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="service-country-select"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="CA">Canada</SelectItem>
              </SelectContent>
            </Select>
            {form.country && (
              <Input
                value={form.postal_code}
                onChange={(e) => set("postal_code", e.target.value)}
                placeholder={form.country === "CA" ? "Your postal code (e.g. M5V)" : "Your ZIP code (e.g. 10001)"}
                data-testid="service-postal-input"
              />
            )}
          </div>

          {!isRequest && form.country && !form.remote_ok && (
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Travel anywhere in {form.country === "CA" ? "Canada" : "the US"}</p>
                  <p className="text-xs text-muted-foreground">I'll serve the whole country</p>
                </div>
                <Switch checked={form.coverage_countrywide} onCheckedChange={(v) => set("coverage_countrywide", v)} data-testid="service-countrywide-switch" />
              </div>
              {!form.coverage_countrywide && (
                <div className="mt-3">
                  <Label htmlFor="svc-radius">Travel radius (miles from your postal code)</Label>
                  <Input id="svc-radius" type="number" min="0" value={form.travel_radius_miles} onChange={(e) => set("travel_radius_miles", e.target.value)} placeholder="e.g. 50" className="mt-1" data-testid="service-radius-input" />
                </div>
              )}
            </div>
          )}
          <div>
            <Label htmlFor="svc-loc">City / area label (optional)</Label>
            <Input id="svc-loc" data-testid="service-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Austin, TX" className="mt-1" />
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Photos (optional)</Label>
          <div className="flex flex-wrap gap-3">
            {images.map((img, i) => (
              <div key={i} className="relative h-20 w-20 overflow-hidden rounded-lg border border-border">
                <img src={img} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))} className="absolute right-0.5 top-0.5 rounded bg-black/70 p-0.5 text-white">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {images.length < 5 && (
              <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary">
                <ImagePlus className="h-5 w-5" />
                <input type="file" accept="image/*" multiple className="hidden" onChange={addImages} data-testid="service-image-input" />
              </label>
            )}
          </div>
        </div>

        <Button onClick={submit} disabled={saving} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="service-submit-button">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {isRequest ? "Post request" : "Post service"}
        </Button>
      </Card>
    </div>
  );
}
